import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { handleLogout } from '../data/coreActions';
import Toast from '../utils/Toast';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const MEDIA_CHECK_INTERVAL_MS = 30 * 1000;
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

// True if any <video>/<audio> on the page is currently playing — watching
// a lesson video or listening to audio is real engagement even though it
// generates no mouse/keyboard events for many minutes at a time.
const hasPlayingMedia = () =>
  Array.from(document.querySelectorAll('video, audio')).some(
    (el) => !(el as HTMLMediaElement).paused,
  );

// Shared hub computers are the reason this exists: if nobody touches the
// app for 15 minutes, log out so the next person who sits down doesn't
// inherit whatever account was left open. Only runs while `enabled`
// (i.e. actually logged in) is true.
export function useIdleLogout(enabled: boolean) {
  const dispatch = useDispatch();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const logout = () => {
      Toast.error('Logged out due to inactivity. Please log in again.');
      dispatch(handleLogout());
    };

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logout, IDLE_TIMEOUT_MS);
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, resetTimer, { passive: true }),
    );
    resetTimer();

    const mediaCheck = setInterval(() => {
      if (hasPlayingMedia()) resetTimer();
    }, MEDIA_CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, resetTimer),
      );
      if (timerRef.current) clearTimeout(timerRef.current);
      clearInterval(mediaCheck);
    };
  }, [enabled, dispatch]);
}
